import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useMyDiscussions, useMySharerooms } from "@domain/group/hooks";
import { useMyOrganizations } from "@domain/organization/hooks";
import { useModalTools } from "@ui/modals/modalContext";
import { mobile } from "@ui/styles/utils";
import { BoldButton } from "@ui/components/buttons/boldButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Primary, PrimaryBold, PrimaryMedium } from "@ui/components/containers/primary";
import { GroupsModal } from "@ui/components/group/groupsModal";
import { ChevronDownIcon } from "@ui/components/icons";
import { row, stack } from "@ui/components/layout";
import { OrganizationsModal } from "@ui/components/organization/organizationsModal";
import { UserView } from "@ui/components/profile/userView";
import { Tag } from "@ui/components/tag";

const userPagesCount = 2;
type ProfileMenuContentProps = React.HTMLAttributes<HTMLDivElement>;
export const ProfileMenuContent: React.FC<ProfileMenuContentProps> = ({ ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const { trackingService } = useServices();
	const { result: organizations, loading: organizationsLoading } = useMyOrganizations();
	const { result: sharerooms, loading: shareroomsLoading, totalElements: shareroomCount } = useMySharerooms();
	const { result: discussions, loading: discussionsLoading, totalElements: discussionCount } = useMyDiscussions();
	const history = useHistory();

	const { currentProfileService } = useServices();
	const { createModal } = useModalTools();
	const logout = (): void => {
		currentProfileService.logout();
		history.push("/");
	};
	if (!currentProfile) {
		return null;
	}

	return (
		<Container {...htmlProps}>
			<BigUserView
				user={currentProfile}
				pictureSize={"M"}
				unClickable={true}
				renderBottomAccessory={() => (
					<Myprofile
						to={
							currentProfile.id
								? currentProfile.type === "USER"
									? Path.Profile(currentProfile)
									: Path.Organization(currentProfile)
								: ""
						}
					>
						<SecondaryButton light>{I18n.t("profile.self.button")}</SecondaryButton>
					</Myprofile>
				)}
			/>
			{currentProfile.applicationId && (
				<MenuGroup>
					<MyApplication
						to={
							currentProfile.statusApplication === "PENDING_VALIDATION"
								? Path.ApplicationSheet(currentProfile)
								: Path.ApplicationEditor(currentProfile.applicationId, "OMAT")
						}
					>
						<BoldButton>{I18n.t("profile.menu.application")}</BoldButton>
						<Tag label="OMAT" />
					</MyApplication>
				</MenuGroup>
			)}
			<MenuGroup>
				<Link to={Path.Profile(currentProfile, "content", true)}>
					<BoldButton>{I18n.t("profile.menu.courses")}</BoldButton>
				</Link>
				<Link to={Path.Profile(currentProfile, "favorite", true)}>
					<BoldButton>{I18n.t("profile.menu.favorite")}</BoldButton>
				</Link>
			</MenuGroup>
			<Separator />
			{shareroomCount !== 0 && (
				<MenuGroup>
					<PrimaryBold>{I18n.t("profile.menu.sharerooms")}</PrimaryBold>
					{sharerooms.length > 0 ? (
						sharerooms.slice(0, userPagesCount).map((shareroom) => (
							<div key={shareroom.id}>
								<Link to={Path.Shareroom(shareroom)}>
									<PageName>{shareroom.name}</PageName>
								</Link>
							</div>
						))
					) : shareroomsLoading ? (
						<ClipLoader size={16} />
					) : null}
					{sharerooms.length > userPagesCount && (
						<TertiaryButton
							onClick={() =>
								createModal((close) => (
									<GroupsModal groups={sharerooms} loading={shareroomsLoading} type={"SHAREROOM"} close={close} />
								))
							}
						>
							{I18n.t("listing.more", { count: sharerooms.length - userPagesCount })}
						</TertiaryButton>
					)}
				</MenuGroup>
			)}
			{discussionCount !== 0 && (
				<MenuGroup>
					<PrimaryBold>{I18n.t("profile.menu.discussions")}</PrimaryBold>
					{discussions.length > 0 ? (
						discussions.slice(0, userPagesCount).map((discussion) => (
							<div key={discussion.id}>
								<Link to={Path.Discussion(discussion)}>
									<PageName>{discussion.name}</PageName>
								</Link>
							</div>
						))
					) : discussionsLoading ? (
						<ClipLoader size={16} />
					) : null}
					{discussions.length > userPagesCount && (
						<TertiaryButton
							onClick={() =>
								createModal((close) => (
									<GroupsModal
										groups={discussions}
										loading={discussionsLoading}
										type={"DISCUSSION_GROUP"}
										close={close}
									/>
								))
							}
						>
							{I18n.t("listing.more", { count: discussions.length - userPagesCount })}
						</TertiaryButton>
					)}
				</MenuGroup>
			)}
			<MenuGroup>
				<PrimaryBold>{I18n.t("profile.menu.organizations")}</PrimaryBold>
				{organizations.length > 0 ? (
					organizations.slice(0, userPagesCount).map((organization) => (
						<div key={organization.id}>
							<Link to={Path.Organization(organization)}>
								<PageName>{organization.primaryName}</PageName>
							</Link>
						</div>
					))
				) : organizationsLoading ? (
					<ClipLoader size={16} />
				) : null}
				{organizations.length > userPagesCount && (
					<TertiaryButton onClick={() => createModal((close) => <OrganizationsModal close={close} />)}>
						{I18n.t("listing.more", { count: organizations.length - userPagesCount })}
					</TertiaryButton>
				)}
				<RedLink>
					<Link
						to={Path.NewOrganization}
						onClick={() => trackingService.track("Navbar - profile - Create a professional page")}
					>
						{I18n.t("organization.create.title")}
					</Link>
				</RedLink>
			</MenuGroup>
			<Separator />
			<MenuGroup>
				<SecondaryLink to={Path.Settings}>
					<PrimaryMedium>{I18n.t("profile.menu.settings")}</PrimaryMedium>
					<ChevronDownIcon />
				</SecondaryLink>
			</MenuGroup>
			{/* {isAdmin && (
				<RedLink>
					<Link to={Path.Backoffice}>{I18n.t("profile.menu.backoffice")}</Link>
				</RedLink>
			)} */}
			<DisconnectButton onClick={() => logout()}>{I18n.t("logout")}</DisconnectButton>
		</Container>
	);
};

const Container = styled.div`
	${stack("S", "flex-start", "stretch")};
	min-width: 236px;
	padding: 36px 20px 25px;
	width: 400px;
	max-width: 100vw;
	overflow: hidden;
	@media ${mobile} {
		padding: 16px 16px 32px;
	}
	text-overflow: ellipsis;
`;

const Myprofile = styled(Link)`
	button {
		margin-top: 5px;
		font-size: 0.8125rem;
		padding: 8px 12px;
	}
`;

const MyApplication = styled(Link)`
	display: flex;
	gap: 15px;
`;

const BigUserView = styled(UserView)`
	${({ theme }) => theme.textStyles.subtitle};
`;

const MenuGroup = styled.div`
	${stack("S")};
	margin: 10px 0;
`;

const PageName = styled(Primary)`
	cursor: pointer;
	transition: color 0.1s linear;
	&:hover {
		color: ${({ theme }) => theme.colors.gray};
	}
`;

const RedLink = styled(Primary)`
	color: ${({ theme }) => theme.colors.primary};
	font-weight: 700;
`;

const SecondaryLink = styled(Link)`
	align-self: stretch;
	${row(2, "space-between", "center")};

	svg {
		transform: rotate(-90deg);
		path {
			fill: ${({ theme }) => theme.colors.textSecondary};
		}
	}

	> div {
		color: ${({ theme }) => theme.colors.textSecondary};
	}
`;

const DisconnectButton = styled(TertiaryButton)`
	align-self: flex-start;
	margin-top: 10px;
	text-decoration: none;
	${({ theme }) => theme.textStyles.label};
	font-size: 0.9375rem;

	@media ${mobile} {
		margin-bottom: 40px;
	}
`;

const Separator = styled.div`
	align-self: stretch;
	flex-shrink: 0;
	background-color: ${({ theme }) => theme.colors.lightergray};
	height: 1px;
	margin: 0 -20px;
`;
